import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";
// Hand-rolled search validator (same shape/behaviour as the previous zod
// schema). validateSearch is critical route code, so keeping zod out of it
// removes ~128KB (raw) from the bundle every page downloads.
const SEARCH_KEYS = [
  "first_name",
  "last_name",
  "email",
  "phone",
  "address",
  "city",
  "state",
  "postal_code",
  "property_type",
  "services",
  "timeline",
  "notes",
] as const;

type SearchParams = Record<(typeof SEARCH_KEYS)[number], string>;

function validateSearch(search: Record<string, unknown>): SearchParams {
  const out = {} as SearchParams;
  for (const key of SEARCH_KEYS) {
    const value = search?.[key];
    out[key] = typeof value === "string" ? value : "";
  }
  return out;
}

export const Route = createFileRoute("/thank-you")({
  validateSearch,
  head: () => ({
    meta: [
      { title: "Thank You — Capital Pro Pressure Washing" },
      { name: "robots", content: "noindex, nofollow" },
      {
        name: "description",
        content:
          "Thanks for requesting a free quote from Capital Pro Pressure Washing. Book your quick 10-minute quote call for today or tomorrow.",
      },
    ],
    links: [
      { rel: "preconnect", href: "https://go.capitalproeliteservices.com" },
      { rel: "preconnect", href: "https://link.msgsndr.com" },
    ],
  }),
  component: ThankYou,
});

const BOOKING_ID = "fMwpj0GslUDSEyhFN2jf";
const BOOKING_BASE = `https://go.capitalproeliteservices.com/widget/booking/${BOOKING_ID}`;

function ThankYou() {
  const search = Route.useSearch();
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  // GoHighLevel's inline form embed handles its own height via form_embed.js.
  useEffect(() => {
    const src = "https://go.capitalproeliteservices.com/js/form_embed.js";
    if (document.querySelector(`script[src="${src}"]`)) return;
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    document.body.appendChild(script);
  }, []);

  // After picking a time slot the widget swaps to its details step and posts a
  // new height. That step renders at the TOP of the frame, so if the lead
  // picked a late slot they're left staring at blank space. Scroll them back
  // to the top of the calendar whenever the frame height changes.
  useEffect(() => {
    let lastHeight = 0;
    function onMessage(event: MessageEvent) {
      let host = "";
      try {
        host = new URL(event.origin).hostname;
      } catch {
        return;
      }
      if (!/(msgsndr|capitalproeliteservices)\.com$/.test(host)) return;
      const el = frameRef.current;
      if (!el) return;
      const height = el.getBoundingClientRect().height;
      if (Math.abs(height - lastHeight) < 40) return;
      lastHeight = height;
      if (el.getBoundingClientRect().top < 0) {
        window.scrollTo({
          top: el.getBoundingClientRect().top + window.scrollY - 12,
          behavior: "smooth",
        });
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const bookingSrc = useMemo(() => {
    const params = new URLSearchParams();
    if (search.first_name) params.set("first_name", search.first_name);
    if (search.last_name) params.set("last_name", search.last_name);
    if (search.email) params.set("email", search.email);
    if (search.phone) params.set("phone", search.phone);
    if (search.address) params.set("address", search.address);
    if (search.city) params.set("city", search.city);
    if (search.state) params.set("state", search.state);
    if (search.postal_code) params.set("postal_code", search.postal_code);
    if (search.property_type) params.set("property_type", search.property_type);
    if (search.services) params.set("services", search.services);
    if (search.timeline) params.set("timeline", search.timeline);
    if (search.notes) params.set("notes", search.notes);
    const qs = params.toString();
    return qs ? `${BOOKING_BASE}?${qs}` : BOOKING_BASE;
  }, [
    search.first_name,
    search.last_name,
    search.email,
    search.phone,
    search.address,
    search.city,
    search.state,
    search.postal_code,
    search.property_type,
    search.services,
    search.timeline,
    search.notes,
  ]);

  return (
    <main className="min-h-[70vh] bg-[var(--brand-light-gray)] py-10 md:py-16 px-4">
      <div className="mx-auto max-w-[880px] w-full">
        <div className="text-center mb-5">
          <p
            className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--brand-red)] mb-1.5"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Request Received — Last Step
          </p>
          <h1
            className="text-2xl md:text-3xl font-bold uppercase text-[var(--brand-deep-blue)] tracking-tight"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Pick a time for your quote call
          </h1>
          <p className="mt-2 text-sm md:text-base text-[var(--brand-charcoal)]/80 max-w-[540px] mx-auto">
            Quick <strong>10-minute phone call</strong> — most quotes are handled
            right on the phone. Grab a weekday slot, today or tomorrow if it's
            open. If we find your property needs a closer look, we'll schedule a
            free in-person visit at no extra cost.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <iframe
            ref={frameRef}
            src={bookingSrc}
            id={BOOKING_ID}
            title="Book your 10-minute quote call"
            allow="payment"
            scrolling="no"
            style={{
              width: "100%",
              border: "none",
              overflow: "hidden",
              display: "block",
              minHeight: "600px",
            }}
          />
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-[var(--brand-charcoal)]/70 max-w-[560px] mx-auto mb-4">
            Calls are booked Monday–Friday. If the call shows your property needs
            eyes on it, we'll book a free in-person visit right then — no
            obligation and no charge either way.
          </p>
          <p className="text-[var(--brand-charcoal)]/80">
            Don't see a time that works?{" "}
            <a
              href="tel:+15189001913"
              className="font-bold text-[var(--brand-deep-blue)] underline underline-offset-4 hover:text-[var(--brand-red)] transition"
            >
              Call (518) 900-1913
            </a>
          </p>
          <p className="mt-2 text-sm text-[var(--brand-charcoal)]/60">
            No pressure — if you don't book, we'll still reach out shortly.
          </p>
        </div>
      </div>
    </main>
  );
}